import { useCallback, useEffect, useRef, useState } from 'react';
import { ExternalLink, GripVertical, Plus } from 'lucide-react';
import { DragHandle } from '@tiptap/extension-drag-handle-react';
import type { Editor } from '@tiptap/react';
import type { Node as PMNode } from '@tiptap/pm/model';

// ---------------------------------------------------------------------------
// Block handles (left gutter) — drag to reorder + "+" to insert a block below
// ---------------------------------------------------------------------------
/** Floating left-margin control shown next to the hovered block: a "+" button
 *  that inserts an empty paragraph below and opens the slash menu, plus a grip
 *  that drags the whole block to reorder it (handled natively by the plugin). */
export function BlockHandles({ editor }: { editor: Editor }) {
  // Tracks the block the handle currently points at (updated on hover).
  const target = useRef<{ node: PMNode | null; pos: number }>({ node: null, pos: 0 });

  // MUST be stable: DragHandle re-registers its ProseMirror plugin whenever this
  // ref changes, and a new plugin registration reconfigures the editor state —
  // which would tear down any in-flight suggestion (slash / @mention) menu on
  // every keystroke (BlockEditor re-renders as its word count updates).
  const onNodeChange = useCallback(({ node, pos }: { node: PMNode | null; pos: number }) => {
    target.current = { node, pos };
  }, []);

  const insertBelow = () => {
    const { node, pos } = target.current;
    if (!node) return;
    // Insert a fresh paragraph after the hovered block, drop the caret inside
    // it, then type "/" so the slash menu opens ready for a block choice.
    const after = pos + node.nodeSize;
    editor.chain().insertContentAt(after, { type: 'paragraph' }).focus(after + 1).insertContent('/').run();
  };

  return (
    <DragHandle editor={editor} onNodeChange={onNodeChange} className="flex items-center gap-0.5">
      <button
        type="button"
        aria-label="Insert block below"
        title="Insert block below"
        // Keep the click from arming the drag plugin bound to this element.
        onPointerDown={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={insertBelow}
        className="text-muted-foreground hover:bg-muted hover:text-foreground grid size-5 place-items-center rounded"
      >
        <Plus className="size-4" />
      </button>
      <span
        aria-label="Drag to move"
        title="Drag to move"
        className="text-muted-foreground hover:bg-muted hover:text-foreground grid size-5 cursor-grab place-items-center rounded active:cursor-grabbing"
      >
        <GripVertical className="size-4" />
      </span>
    </DragHandle>
  );
}

// ---------------------------------------------------------------------------
// Hover link preview — floating card showing the target URL + "open"
// ---------------------------------------------------------------------------
interface HoverState {
  href: string;
  left: number;
  top: number;
}

/** Shows a small card with the link's URL (and an open button) whenever the
 *  cursor hovers a link in the document. Read-only affordance — editing stays
 *  in the selection bubble menu. */
export function LinkPreview({ editor }: { editor: Editor }) {
  const [hover, setHover] = useState<HoverState | null>(null);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const root = editor.view.dom;
    const clearHide = () => {
      if (hideTimer.current) {
        clearTimeout(hideTimer.current);
        hideTimer.current = null;
      }
    };
    const scheduleHide = () => {
      clearHide();
      hideTimer.current = setTimeout(() => setHover(null), 200);
    };
    const onOver = (e: Event) => {
      const anchor = (e.target as HTMLElement | null)?.closest('a[href]');
      if (!anchor) return;
      clearHide();
      const rect = anchor.getBoundingClientRect();
      setHover({ href: (anchor as HTMLAnchorElement).href, left: rect.left, top: rect.bottom + 6 });
    };
    const onOut = (e: Event) => {
      if ((e.target as HTMLElement | null)?.closest('a[href]')) scheduleHide();
    };
    root.addEventListener('mouseover', onOver);
    root.addEventListener('mouseout', onOut);
    return () => {
      clearHide();
      root.removeEventListener('mouseover', onOver);
      root.removeEventListener('mouseout', onOut);
    };
  }, [editor]);

  if (!hover) return null;

  const label = hover.href.replace(/^https?:\/\//, '').replace(/\/$/, '');
  return (
    <div
      className="bg-popover text-popover-foreground fixed z-50 flex max-w-sm items-center gap-2 rounded-md border px-2 py-1 text-xs shadow-md"
      style={{ left: hover.left, top: hover.top }}
      onMouseEnter={() => {
        if (hideTimer.current) clearTimeout(hideTimer.current);
      }}
      onMouseLeave={() => setHover(null)}
    >
      <span className="truncate">{label}</span>
      <a
        href={hover.href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-primary hover:bg-muted grid size-5 shrink-0 place-items-center rounded"
        aria-label="Open link in new tab"
        title="Open link in new tab"
      >
        <ExternalLink className="size-3.5" />
      </a>
    </div>
  );
}
